import { useState } from "react"
import { Link } from "react-router-dom"
import {
  AbilityCard,
  AchievementCard,
  ActivityItem,
  ApprovalCard,
  AudienceSelector,
  CurrencyAmount,
  CurrencyCard,
  NotificationItem,
  OfferCard,
  PersonAvatar,
  PersonChip,
  RewardPreview,
  TaskCard,
  type FamilyAudience,
  type FamilyPerson,
  type TaskUiStatus
} from "../components/family"
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  PageHeader,
  Section,
  Sheet,
  Textarea
} from "../components/ui"
import { Icon } from "../components/Icon"
import { TaskMediaGallery } from "../features/tasks/TaskMediaGallery"

const people: FamilyPerson[] = [
  { id: "nik", displayName: "Ник" },
  { id: "sonya", displayName: "Соня" },
  { id: "vasya", displayName: "Вася" },
  { id: "masha", displayName: "Маша" },
  { id: "anya", displayName: "Анна Мария" }
]

const taskStates: Array<{ status: TaskUiStatus; title: string; description?: string; reward?: string }> = [
  { status: "OPEN", title: "Убрать кухню", description: "Протереть стол и загрузить посудомойку", reward: "5 💆" },
  { status: "CLAIMABLE", title: "Разобрать продукты", description: "Эту задачу может взять кто-нибудь один" },
  { status: "CLAIMED", title: "Полить цветы", description: "Соня взяла задачу" },
  { status: "OVERDUE", title: "Забрать посылку", description: "Срок прошёл, но задачу ещё можно закрыть" },
  { status: "SUBMITTED", title: "Помыть велосипед", description: "Отчёт отправлен Нику", reward: "10 ⭐" },
  { status: "COMPLETED", title: "Сложить чистую одежду" },
  { status: "REJECTED", title: "Навести порядок на рабочем столе", description: "Нужно добавить фото результата" },
  { status: "MISSED", title: "Вынести бумагу на переработку" },
  { status: "SKIPPED", title: "Помыть посуду" },
  { status: "OPEN", title: "Очень длинное название семейной задачи, которое спокойно переносится на несколько строк и не ломает действия", description: "Проверка длинного содержимого" }
]

export function UiGalleryPage() {
  const [audience, setAudience] = useState<FamilyAudience>({ memberIds: ["sonya", "vasya"], groupIds: ["all"] })
  const [dialog, setDialog] = useState(false)
  const [sheet, setSheet] = useState(false)
  return (
    <main className="ui-gallery">
      <PageHeader eyebrow="Development · UI system v1" title="Семья — UI gallery" description="Исполняемая спецификация foundations, product patterns и representative states." />

      <Section title="Foundations" description="Semantic tokens, typography, spacing and controls.">
        <div className="gallery-subsection">
          <h3>Цвета</h3>
          <div className="token-grid">
            {["background", "surface", "surface-muted", "primary", "success", "warning", "danger", "info"].map((token) => <div key={token}><i style={{ background: `var(--${token})` }} /><span>{token}</span></div>)}
          </div>
        </div>
        <div className="gallery-subsection typography-sample">
          <span className="type-display">Всё важное рядом</span>
          <h1>Заголовок страницы</h1><h2>Заголовок раздела</h2><h3>Заголовок карточки</h3>
          <p>Основной текст интерфейса — спокойный, читаемый и без технических терминов.</p>
          <small>Подпись и вторичная информация</small>
        </div>
        <div className="gallery-subsection">
          <h3>Spacing</h3>
          <div className="spacing-sample">{[4, 8, 12, 16, 20, 24, 32, 40, 48].map((space) => <span key={space} style={{ width: space, height: space }}>{space}</span>)}</div>
        </div>
        <div className="gallery-subsection">
          <h3>Buttons, inputs, dialog / sheet</h3>
          <div className="button-row"><Button>Основное действие</Button><Button variant="secondary">Вторичное</Button><Button variant="ghost">Тихое</Button><Button variant="danger">Удалить</Button><Button loading>Сохраняем</Button></div>
          <div className="gallery-form-grid">
            <Field label="Название" hint="Подсказка остаётся рядом с полем"><Input placeholder="Например, убрать кухню" /></Field>
            <Field label="Комментарий" error="Напишите, что нужно исправить"><Textarea rows={2} /></Field>
          </div>
          <div className="button-row"><Button variant="secondary" onClick={() => setDialog(true)}>Открыть dialog</Button><Button variant="secondary" onClick={() => setSheet(true)}>Открыть sheet</Button></div>
        </div>
      </Section>

      <Section title="People" description="Один avatar fallback и один chip pattern.">
        <div className="gallery-row">{people.map((person) => <PersonChip key={person.id} person={person} />)}<PersonAvatar person={{ id: "long", displayName: "Очень Длинное Имя" }} size="lg" /></div>
        <AudienceSelector label="Кому" value={audience} onChange={setAudience} members={people} groups={[{ id: "all", name: "Все", isSystem: true }, { id: "home", name: "Дом на этой неделе" }]} onCreateGroup={() => undefined} />
      </Section>

      <Section title="Tasks" description="Open, deadline, overdue, claim, approval and terminal states.">
        <div className="gallery-list">{taskStates.map((task, index) => <TaskCard key={`${task.status}:${index}`} {...task} dueLabel={index % 3 === 0 ? "Сегодня, 20:00" : undefined} people={index === 9 ? people.slice(0, 3) : people.slice(0, index % 3)} participantOverflow={index === 9 ? 7 : 0} actions={["OPEN", "CLAIMABLE", "REJECTED"].includes(task.status) ? <Button size="sm">{task.status === "CLAIMABLE" ? "Взять задачу" : task.status === "REJECTED" ? "Исправить отчёт" : "Отправить результат"}</Button> : undefined} />)}</div>
        <div className="gallery-subsection">
          <h3>Вложения к отчёту</h3>
          <TaskMediaGallery
            media={[
              { url: "/icons/icon-512.png", type: "IMAGE" },
              { url: "/icons/icon-maskable-512.png", type: "IMAGE" }
            ]}
          />
        </div>
      </Section>

      <Section title="Currencies & rewards">
        <div className="gallery-card-grid">
          <CurrencyCard name="Массиры" symbol="💆" icon="💆" amount="20" color="#7c68d9" actions={<Button size="sm">Потратить</Button>} />
          <CurrencyCard name="Звёзды" symbol="⭐" icon="⭐" amount="0" color="#d79b3f" />
          <CurrencyCard name="Очень длинное название семейной валюты" symbol="🌿" icon="🌿" amount="1250" color="#3e8c69" restricted />
        </div>
        <div className="gallery-row"><CurrencyAmount amount={5} symbol="⭐" signed /><CurrencyAmount amount={15} symbol="💆" name="Массиры" /><Badge tone="warning">Запрос на трату · баланс не изменён</Badge></div>
        <RewardPreview items={[{ icon: "💆", label: "Массиры", amount: 5 }, { icon: "✨", label: "Не мыть посуду" }, { icon: "🏆", label: "Помощник недели" }]} />
      </Section>

      <Section title="Offers, abilities & achievements">
        <div className="gallery-card-grid">
          <OfferCard icon="🍽️" title="Не мыть посуду" description="Одна способность после покупки" price={<CurrencyAmount amount={50} symbol="⭐" />} action={<Button size="sm">Купить за 50 ⭐</Button>} />
          <OfferCard icon="🛋️" title="Очень длинное название предложения для проверки переноса текста" price={<CurrencyAmount amount={200} symbol="⭐" />} affordable={false} action={<Button size="sm" disabled>Недоступно</Button>} />
          <AbilityCard icon="✨" title="Выбрать фильм" description="Один раз выбрать фильм для семейного вечера" rarity="RARE" count={3} action={<Button size="sm">Использовать</Button>} />
          <AbilityCard icon="⏭️" title="Пропустить задачу" description="Пропускает одну вашу текущую задачу" rarity="EPIC" status="Ждёт подтверждения" />
          <AbilityCard icon="👑" title="Королевский выходной" rarity="LEGENDARY" status="Доступна" />
          <AchievementCard icon="🏆" title="Помощник недели" description="За заботу о доме и близких" rarity="RARE" awarded />
          <AchievementCard icon="🌱" title="Первый маленький шаг" description="Выполнить первую задачу" rarity="COMMON" />
        </div>
      </Section>

      <Section title="Approvals, activity & notifications">
        <div className="gallery-list">
          <ApprovalCard icon="✅" person={people[1]} title="Соня завершила «Убрать кухню»" description="Есть комментарий и 3 фотографии. Награда будет выдана после подтверждения." actions={<div className="button-row"><Button size="sm" variant="ghost">Вернуть</Button><Button size="sm">Принять</Button></div>} />
          <ActivityItem icon="💆" title="Соня получила +5 💆" body="За задачу «Убрать кухню»" time="Сегодня, 18:42" trailing={<CurrencyAmount amount={5} symbol="💆" signed />} />
          <NotificationItem icon="🪙" title="Ник просит подтвердить трату" body="5 Массиров · баланс пока не изменился" time="5 минут назад" unread action={<Button size="sm">Посмотреть</Button>} />
        </div>
      </Section>

      <Section title="Empty, loading, errors & offline">
        <div className="gallery-state-grid"><EmptyState icon="☀️" title="Пока нет задач на сегодня" text="Можно отдохнуть или добавить новую." action={<Button size="sm">Добавить задачу</Button>} /><div className="state-surface"><LoadingState rows={3} /></div><ErrorState message="Не удалось загрузить этот раздел. Введённые данные не потеряны." retry={() => undefined} /><ErrorState title="Сейчас нет сети" message="Покажем свежие данные, когда соединение восстановится." /></div>
      </Section>

      <Section title="Production form flows" description="Gallery не дублирует production-формы: ссылки открывают те же маршруты, которыми пользуется приложение.">
        <div className="button-row">
          <Link className="button button--primary button--md" to="/tasks/new">Task create</Link>
          <Link className="button button--secondary button--md" to="/family/config/currency/new">Currency create</Link>
          <Link className="button button--secondary button--md" to="/family/config/scheduled-grant/new">Scheduled grant create</Link>
        </div>
      </Section>

      <Dialog open={dialog} onClose={() => setDialog(false)} title="Короткое действие" eyebrow="Desktop dialog"><p>На телефоне этот же action pattern становится bottom sheet.</p><div className="dialog__actions"><Button variant="ghost" onClick={() => setDialog(false)}>Отмена</Button><Button onClick={() => setDialog(false)}>Подтвердить</Button></div></Dialog>
      <Sheet open={sheet} onClose={() => setSheet(false)} title="Потратить 5 💆" description="Баланс изменится только после подтверждения Ника"><div className="outcome-preview"><span>Сейчас <strong>20 💆</strong></span><Icon name="chevronRight" /><span>После подтверждения <strong>15 💆</strong></span></div><div className="dialog__actions"><Button variant="ghost" onClick={() => setSheet(false)}>Отмена</Button><Button onClick={() => setSheet(false)}>Отправить запрос</Button></div></Sheet>
    </main>
  )
}
